import React, { SyntheticEvent, useCallback, useEffect, useMemo, useState } from "react";

import { Button, Form, Icon, Label } from "semantic-ui-react";

import moment from "moment";
import { ComponentPropsGetterR } from "react-table-6";

import ButtonLoadCheck from "react-lib/appcon/common/ButtonLoadCheck";
import SearchBoxWithKey from "react-lib/appcon/common/SearchBoxWithKey";

import ModConfirm from "react-lib/apps/common/cnmi/ModConfirm";
import SnackMessage from "react-lib/apps/common/SnackMessage";

import LoadingIcon from "../HCU/LoadingIcon";

import CardRehabWorkListUX from "./CardRehabWorkListUX";

import {
  ACTIONS,
  BTN_ACTS,
  EditCompleteParamsType,
  MasterOptions,
  OnEvent,
  PickedState,
  RehabWorkListType,
  RunSequence,
  SEQUENCE_NAME,
  SetProp,
  State,
} from "./sequence/RehabWorkList";

import { formatDate, formatDatetime } from "react-lib/utils/dateUtils";

// Types
type CardRehabDeviceProps = Pick<
  PickedState,
  "buttonLoadCheck" | "errorMessage" | "searchedItemListWithKey"
> & {
  onEvent: OnEvent;
  setProp: SetProp;
  runSequence: RunSequence;
  RehabWorkListSequence: State["RehabWorkListSequence"];
  languageUX: "en" | "th";
  masterOptions: MasterOptions;
};

const IMAGES = {
  complete: "/static/images/rehab/complete-icon.png",
  uncomplete: "/static/images/rehab/uncomplete-icon.png",
};

const ENCOUNTER_TYPE_OPTIONS = [
  { key: "IPD", text: "IPD", value: "IPD" },
  { key: "OPD", text: "OPD", value: "OPD" },
];

const CardRehabWorkList = (props: CardRehabDeviceProps) => {
  const [modEditPhysiologist, setModEditPhysiologist] = useState<{
    orders: RehabWorkListType["orders"];
    physiotherapist: number | null;
    physiotherapist_full_name: string | null;
  } | null>(null);

  useEffect(() => {
    props.runSequence({ sequence: "RehabWorkList", restart: true });

    return () => {
      props.setProp(`errorMessage.${SEQUENCE_NAME}`, null);
    };
  }, []);

  const handleUpdateStatus = useCallback((data: EditCompleteParamsType) => {
    props.runSequence({ sequence: "RehabWorkList", action: ACTIONS.EDIT_COMPLETE, ...data });
  }, []);

  const formattedItems = useMemo(
    () =>
      (props.RehabWorkListSequence?.items ?? []).map((item, index) => {
        const isNew = item.orders.some((acc) => acc.is_new);
        const isAppointment = item.orders.some((acc) => acc.is_appointment);
        const hideFromQueue = item.orders.some((acc) => acc.hide_from_queue);

        return {
          ...item,
          action: !item.continue_start_date && (
            <div style={{ alignItems: "center", display: "flex", flexDirection: "column" }}>
              <Button
                style={{ padding: "3.5px" }}
                color={hideFromQueue ? "red" : "green"}
                icon
                onClick={(e) => {
                  e.stopPropagation();

                  handleUpdateStatus({
                    hide_from_queue: !hideFromQueue,
                    index,
                    orders: item.orders,
                  });
                }}
              >
                <LoadingIcon
                  style={{ width: "17px" }}
                  dimmerStyle={{ marginLeft: "-4px", marginTop: "1px" }}
                  img={hideFromQueue ? IMAGES.uncomplete : IMAGES.complete}
                  loading={!!props.buttonLoadCheck?.[`${BTN_ACTS.EDIT_COMPLETE}_${index}`]}
                />
              </Button>
              <label
                style={{
                  color: hideFromQueue ? "rgba(217, 2, 2, 1)" : "rgba(27, 157, 44, 1)",
                  fontSize: "0.8rem",
                  fontWeight: "bold",
                  lineHeight: 1.25,
                  marginTop: "0.15rem",
                }}
              >
                {hideFromQueue ? "Reverse to Incomplete" : "COMPLETE"}
              </label>
            </div>
          ),
          date: (
            <>
              {item.continue_start_date
                ? `Continue for ${formatDate(moment(item.continue_start_date))}`
                : formatDatetime(item.en_started, true)}
            </>
          ),
          hnEncounter: `${item.hn}\n(EN : ${item.en_number})`,
          patient: (
            <div style={{ textAlign: "left" }}>
              <div>{item.patient_name}</div>
              {(isNew || isAppointment) && (
                <Label color={isAppointment ? "yellow" : "green"} size="mini">
                  {isAppointment ? "APPOINTMENT" : "NEW"}
                </Label>
              )}
            </div>
          ),
          physiotherapist: (
            <div style={{ columnGap: "5px", display: "grid", gridTemplateColumns: "1fr auto" }}>
              <div>{item.orders[0].physiotherapist_full_name}</div>
              <div>
                <Icon
                  color="yellow"
                  name="edit outline"
                  link
                  onClick={(e: SyntheticEvent) => {
                    e.stopPropagation();

                    setModEditPhysiologist({
                      orders: item.orders,
                      physiotherapist: item.orders[0].physiotherapist,
                      physiotherapist_full_name: item.orders[0].physiotherapist_full_name,
                    });
                  }}
                />
              </div>
            </div>
          ),
        };
      }),
    [props.buttonLoadCheck, props.RehabWorkListSequence?.items]
  );

  const handleChangeFilter = (e: SyntheticEvent | null, data: Record<string, any>) => {
    const name = data.name;
    const value = data.type === "checkbox" ? data.checked : data.value;

    if (!name) {
      return;
    }

    props.setProp("RehabWorkListSequence.filter", {
      ...props.RehabWorkListSequence?.filter,
      [name]: value,
    });
  };

  const handleSearch = () => {
    props.runSequence({ sequence: "RehabWorkList", action: ACTIONS.SEARCH });
  };

  const handleCloseErrMsg = () => {
    props.setProp(`errorMessage.${SEQUENCE_NAME}`, null);
  };

  const handleGetTdProps = () => ({
    style: {
      textAlign: "center",
    },
  });

  const handleGetTrProps: ComponentPropsGetterR = (state, rowInfo) => ({
    onClick: () => {
      props.runSequence({
        sequence: "RehabWorkList",
        action: ACTIONS.SELECT,
        data: rowInfo?.original,
        encounterId: rowInfo?.original?.en_id,
      });
    },
  });

  const handleCloseModEditPhysiologist = () => {
    setModEditPhysiologist(null);
  };

  const handleChangePhysiologist = (value: any) => {
    if (modEditPhysiologist) {
      setModEditPhysiologist({
        ...modEditPhysiologist,
        physiotherapist: value,
        physiotherapist_full_name: "",
      });
    }
  };

  const handleConfirmEdit = () => {
    if (!modEditPhysiologist) {
      return;
    }

    props.runSequence({
      sequence: "RehabWorkList",
      action: ACTIONS.EDIT_PHYSIOLOGIST,
      orders: modEditPhysiologist.orders,
      physiotherapist: modEditPhysiologist.physiotherapist,
      onSuccess: handleCloseModEditPhysiologist,
    });
  };

  console.log("CardRehabWorkList props:", props);

  return (
    <div>
      <SnackMessage
        onEvent={props.onEvent}
        error={props.errorMessage?.[SEQUENCE_NAME]}
        success={null}
        onClose={handleCloseErrMsg}
      />

      <CardRehabWorkListUX
        filter={props.RehabWorkListSequence?.filter}
        items={formattedItems}
        languageUX={props.languageUX}
        divisionOptions={props.masterOptions.division}
        encounterTypeOptions={ENCOUNTER_TYPE_OPTIONS}
        getTdProps={handleGetTdProps}
        getTrProps={handleGetTrProps}
        onChangeFilter={handleChangeFilter}
        buttonSearch={
          <ButtonLoadCheck
            setProp={props.setProp}
            color="blue"
            paramKey={BTN_ACTS.SEARCH}
            size="small"
            title="ค้นหา"
            buttonLoadCheck={props.buttonLoadCheck?.[BTN_ACTS.SEARCH]}
            onClick={handleSearch}
          />
        }
        patientSearchBox={
          <SearchBoxWithKey
            onEvent={props.onEvent}
            id="RWL"
            selectedItem={props.RehabWorkListSequence?.filter?.patient ?? null}
            type="HNFirstLast"
            inline
            searchedItemListWithKey={props.searchedItemListWithKey}
            setSelected={(value) => {
              props.setProp("RehabWorkListSequence.filter.patient", value);
            }}
          />
        }
        physiotherapistSearchBox={
          <SearchBoxWithKey
            onEvent={props.onEvent}
            id="MAR"
            defaultText={props.RehabWorkListSequence?.filter?.physiotherapist_full_name || ""}
            placeholder="Unspecified"
            role="TECHNICIAN_PMR"
            selectedItem={props.RehabWorkListSequence?.filter?.physiotherapist ?? null}
            size="small"
            type="User"
            inline
            searchedItemListWithKey={props.searchedItemListWithKey}
            setSelected={(value) => {
              props.setProp("RehabWorkListSequence.filter", {
                ...props.RehabWorkListSequence?.filter,
                physiotherapist: value,
                physiotherapist_full_name: "",
              });
            }}
          />
        }
      />

      <ModConfirm
        backgroundColor="var(--primary-theme-color)"
        denyButtonStyle={{ marginLeft: "-1rem", width: "8.75rem" }}
        denyButtonText="CLOSE"
        openModal={!!modEditPhysiologist}
        titleName="Assign Physical Therapist"
        onCloseWithDimmerClick={handleCloseModEditPhysiologist}
        onDeny={handleCloseModEditPhysiologist}
        approveButton={
          <ButtonLoadCheck
            setProp={props.setProp}
            style={{ width: "8.75rem" }}
            color="green"
            paramKey={BTN_ACTS.EDIT_PHYSIOLOGIST}
            size="medium"
            title="SAVE"
            basic
            buttonLoadCheck={props.buttonLoadCheck?.[BTN_ACTS.EDIT_PHYSIOLOGIST]}
            onClick={handleConfirmEdit}
          />
        }
        content={
          <Form style={{ margin: "-0.5rem 0 -1rem" }}>
            <Form.Field>
              <label style={{ fontSize: "0.95rem" }}>Please select Physical Therapist</label>
              <SearchBoxWithKey
                onEvent={props.onEvent}
                id="RWL"
                defaultText={modEditPhysiologist?.physiotherapist_full_name || ""}
                role="TECHNICIAN_PMR"
                selectedItem={modEditPhysiologist?.physiotherapist}
                size="small"
                type="User"
                inline
                searchedItemListWithKey={props.searchedItemListWithKey}
                setSelected={handleChangePhysiologist}
              />
            </Form.Field>
          </Form>
        }
      />
    </div>
  );
};

CardRehabWorkList.displayName = "CardRehabWorkList";

export default React.memo(CardRehabWorkList);
